import React, { useState } from "react";
import {
  Alert, Box, Button, CircularProgress, IconButton, MenuItem, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ComprobanteDialog from "./ComprobanteDialog";
import usePaginado from "./usePaginado";
import { PiePaginacion } from "./ListadoTabla";
import { buscarEgresos, fmtFecha, fmtMoney } from "../services/egresosService";

const fieldSx = {
  "& .MuiInputBase-root": { minHeight: 32 },
  "& .MuiInputBase-input": { py: "5px", fontSize: "0.8rem" },
  "& .MuiInputLabel-root": { fontSize: "0.8rem" },
};
const cellSx = { py: 0.2, px: 0.5, fontSize: 11 };

const TIPOS = [
  { value: 0, label: "N° de comprobante" },
  { value: 1, label: "N° de cheque" },
  { value: 2, label: "N° de contrato" },
  { value: 3, label: "N° de referencia" },
];

// Búsqueda personalizada de egresos (espejo de co_cg_busqueda).
export default function BusquedaTab() {
  const [anulado, setAnulado] = useState("no");
  const [tipo, setTipo] = useState(0);
  const [valor, setValor] = useState("");
  const [consulta, setConsulta] = useState(null); // búsqueda aplicada (al presionar Buscar)
  const [comprobanteId, setComprobanteId] = useState(null);
  const paginado = usePaginado(
    (q) => (consulta ? buscarEgresos({ ...consulta, ...q }) : Promise.resolve({ data: { items: [], total: 0 } })),
    [consulta]
  );
  const cargando = paginado.cargando;
  const error = paginado.error;

  const buscar = () => {
    paginado.setPage(1);
    setConsulta({ anulado, tipo, valor: valor.trim() });
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 1, mb: 1, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        <TextField select size="small" label="Anulado" value={anulado} onChange={(e) => setAnulado(e.target.value)} sx={{ ...fieldSx, width: 100 }}>
          <MenuItem value="no">No</MenuItem>
          <MenuItem value="si">Sí</MenuItem>
        </TextField>
        <TextField select size="small" label="Buscar por" value={tipo} onChange={(e) => setTipo(Number(e.target.value))} sx={{ ...fieldSx, width: 200 }}>
          {TIPOS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Valor" value={valor} onChange={(e) => setValor(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && valor.trim()) buscar(); }} sx={{ ...fieldSx, width: 280 }} autoFocus />
        <Button size="small" variant="contained" startIcon={cargando ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
          onClick={buscar} disabled={cargando || !valor.trim()}>
          Buscar
        </Button>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Typography sx={{ px: 1, py: 0.5, fontSize: 12, fontWeight: 800, bgcolor: "action.hover" }}>
          Resultado{consulta ? ` (${paginado.total})` : ""}
        </Typography>
        <TableContainer>
          <Table size="small" sx={{ "& .MuiTableCell-root": cellSx }}>
            <TableHead>
              <TableRow>
                {["ID", "N° cheque", "Dirigido a", "Concepto", "Servicio", "Monto Bs", "Monto USD", "Banco", "Fecha emisión", "Mes de servicio", ""].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!consulta ? (
                <TableRow><TableCell colSpan={11}><Typography variant="body2" color="text.secondary">Ingresa un valor y presiona Buscar.</Typography></TableCell></TableRow>
                ) : !paginado.items.length ? (
                <TableRow><TableCell colSpan={11}><Typography variant="body2" color="text.secondary">No existen egresos.</Typography></TableCell></TableRow>
              ) : (
                <>
                  {paginado.items.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.id}</TableCell>
                      <TableCell>{r.nocheque}</TableCell>
                      <TableCell>{r.nombre}</TableCell>
                      <TableCell>{r.concepto}</TableCell>
                      <TableCell>{r.servicio}</TableCell>
                      <TableCell align="right">{fmtMoney(r.bolivianos)}</TableCell>
                      <TableCell align="right">{fmtMoney(r.dolares)}</TableCell>
                      <TableCell>{r.banco}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtFecha(r.fecha)}</TableCell>
                      <TableCell>{r.mes}</TableCell>
                      <TableCell>
                        <Tooltip title="Ver comprobante">
                          <IconButton size="small" color="primary" onClick={() => setComprobanteId(r.id)} sx={{ p: 0.3 }}>
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 800 }}>Totales de la búsqueda</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{fmtMoney(paginado.datos?.total_bolivianos)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{fmtMoney(paginado.datos?.total_dolares)}</TableCell>
                    <TableCell colSpan={4} />
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {consulta && <PiePaginacion paginado={paginado} />}
      </Paper>

      <ComprobanteDialog id={comprobanteId} onClose={() => setComprobanteId(null)} onCambio={() => paginado.recargar()} />
    </Box>
  );
}
